import React, { useEffect, useState } from "react";
import { Button, Stack, Typography, Divider, IconButton, TextField, Drawer, Accordion, AccordionDetails, AccordionSummary, Alert, CircularProgress } from "@mui/material"
import { Box } from "@mui/material"
import { setAppTheme } from "../Settings/appTheme";
import { useAuth } from "react-oidc-context";
import { StyledIconButton } from "./StyledIconButton";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useMCContext } from "../contexts/useMCStates";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { createCookie } from "../contexts/CookieContext";
import { loadStripe } from "@stripe/stripe-js";
import CreateApi from '../Settings/appSettings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

/** Client clock + timezone for server-side eligibility (discount/credit date windows). */
function getClientEligibilityContext() {
    return {
        clientNowIso: new Date().toISOString(),
        clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
}


interface CartBlockProps {
    props: any;
}


export const CartBlock = ({props}: CartBlockProps) => {

    const auth = useAuth();
    const api = CreateApi();
    const { cartItems, setCartItems, factor, appTheme } = useMCContext();

    const [openCartMenu, setOpenCartMenu] = useState(false);
    const [applicableDiscounts, setApplicableDiscounts] = useState<any[]>([]);
    const [availableCredits, setAvailableCredits] = useState<any[]>([]);
    const [creditAmountToApply, setCreditAmountToApply] = useState<number>(0);
    const [loadingAdjustments, setLoadingAdjustments] = useState(false);

    const cartTotal = cartItems.reduce((sum: number, item: any) => sum + (item.eventDetails.price ?? 0), 0);

    const discountTotal = applicableDiscounts.reduce((sum, d) => {
        if (d.discountType === 'dollar') return sum + d.value;
        return sum + (cartTotal * d.value / 100);
    }, 0);

    const maxCreditsAvailable = availableCredits.reduce((s: number, c: any) => s + (c.remainingAmount ?? 0), 0);

    const safeCreditApplied = Math.min(
        creditAmountToApply,
        maxCreditsAvailable,
        Math.max(0, cartTotal - discountTotal)
    );

    const finalTotal = Math.max(0, cartTotal - discountTotal - safeCreditApplied);

    // Fetch applicable discounts & credits when cart opens
    useEffect(() => {
        if (!openCartMenu || cartItems.length === 0 || !auth.user?.profile?.sub) {
            setApplicableDiscounts([]);
            setAvailableCredits([]);
            setCreditAmountToApply(0);
            return;
        }

        const fetchAdjustments = async () => {
            setLoadingAdjustments(true);
            try {
                const response = await api.post('/mc/GetApplicableDiscountsCredits', {
                    userID: auth.user?.profile?.sub,
                    cartItems: cartItems.map((item: any) => ({
                        eventID: item.eventDetails.eventID,
                        eventGroupIDs: item.eventDetails.eventGroupIDs ?? [],
                        registrationForm: item.registrationForm ?? [],
                    })),
                    ...getClientEligibilityContext(),
                });
                setApplicableDiscounts(response.data.applicableDiscounts ?? []);
                setAvailableCredits(response.data.availableCredits ?? []);
            } catch (err) {
                console.error('Failed to fetch applicable discounts/credits', err);
            } finally {
                setLoadingAdjustments(false);
            }
        };
        fetchAdjustments();
    }, [openCartMenu, cartItems.length]);

    const handleCheckout = async () => {
        try {
            const stripe = await stripePromise;
            const currentUrl = window.location.href;

            // Distribute credit application across entries, earliest-expiring first
            const sortedCredits = [...availableCredits].sort((a, b) => {
                if (!a.expiresAt && !b.expiresAt) return 0;
                if (!a.expiresAt) return 1;
                if (!b.expiresAt) return -1;
                return new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime();
            });

            let remaining = safeCreditApplied;
            const appliedCreditDetails: { creditID: string; tourID?: string; scope: string; amount: number }[] = [];
            for (const credit of sortedCredits) {
                if (remaining <= 0) break;
                const take = Math.min(remaining, credit.remainingAmount);
                if (take > 0) {
                    appliedCreditDetails.push({
                        creditID: credit.creditID,
                        tourID: credit.tourID,
                        scope: credit.scope,
                        amount: Math.round(take * 100) / 100,
                    });
                    remaining -= take;
                }
            }

            const response = await api.post('/mc/create-checkout-session', {
                items: cartItems.map((item: any) => ({
                    id: item.eventDetails.eventID,
                    name: item.eventDetails.eventName,
                    quantity: item.eventDetails.quantity,
                    price: item.eventDetails.price,
                })),
                registrationData: cartItems,
                userID: auth.user?.profile?.sub,
                discountTotal: Math.round(discountTotal * 100) / 100,
                creditApplied: safeCreditApplied,
                appliedDiscountIDs: applicableDiscounts.map(d => d.discountID),
                appliedCreditDetails,
                ...getClientEligibilityContext(),
                success_url: `${window.location.origin}/registration/success?session_id={CHECKOUT_SESSION_ID}&redirect=${encodeURIComponent(window.location.origin)}`,
                cancel_url: currentUrl,
            });

            const { sessionId } = response.data;

            if (!stripe) {
                console.error('Stripe not loaded');
                return;
            }

            const result = await stripe.redirectToCheckout({ sessionId });
            if (result?.error) {
                console.error(result.error);
            }
        } catch (error) {
            console.error('Error in checkout:', error);
        }
    };

    // Credits expiring within 30 days — show at most the two soonest expirations
    const expiringCredits = availableCredits
        .filter((c: any) => {
            if (!c.expiresAt) return false;
            const msIn30Days = 30 * 24 * 60 * 60 * 1000;
            return new Date(c.expiresAt).getTime() - Date.now() < msIn30Days;
        })
        .sort((a: any, b: any) => {
            const ta = new Date(a.expiresAt).getTime();
            const tb = new Date(b.expiresAt).getTime();
            if (ta !== tb) return ta - tb;
            return String(a.creditID).localeCompare(String(b.creditID));
        })
        .slice(0, 2);

    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

    /** Expiry label for cart warnings: M/D/YY in UTC (calendar components). */
    const formatCreditExpiryUtc = (expiresAt: string | Date): string => {
        const d = new Date(expiresAt);
        const month = d.getUTCMonth() + 1;
        const day = d.getUTCDate();
        const yy = (d.getUTCFullYear() % 100).toString().padStart(2, '0');
        return `${month}/${day}/${yy}`;
    };

    const formatDiscountLabel = (d: any): string => {
        const valueStr = d.discountType === 'percentage'
            ? `${d.value}% off`
            : formatCurrency(d.value);
        return `${d.description || 'Discount'} — ${valueStr}`;
    };

    const discountLineAmount = (d: any): number =>
        d.discountType === 'dollar' ? d.value : (cartTotal * d.value / 100);

    return (
        <Box>
            <Box sx={{ position: 'relative' }}>
                <StyledIconButton
                    appTheme={appTheme}
                    label={''}
                    factor={factor}
                    color={'secondary'}
                    width={30*factor}
                    height={30*factor}
                    icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 18*factor }} />}
                    radius={7.5*factor}
                    onClick={() => setOpenCartMenu(true)}
                />
                {cartItems?.length > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -6*factor,
                            right: -6*factor,
                            backgroundColor: appTheme.palette.secondary.main,
                            color: appTheme.palette.primary.main,
                            borderRadius: '50%',
                            width: 15*factor,
                            height: 15*factor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 9*factor,
                        }}
                    >
                        {cartItems.length}
                    </Box>
                )}
            </Box>
            <Drawer
                open={openCartMenu}
                onClose={() => setOpenCartMenu(false)}
                anchor="right"
                sx={{
                    '& .MuiDrawer-paper': {
                        width: '95vw',
                        maxWidth: 600,
                        padding: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }
                }}
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ padding: 1, width: '100%' }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 500, flex: 1, textAlign: 'center' }}>My Cart</Typography>
                    <IconButton aria-label="Close cart" onClick={() => setOpenCartMenu(false)}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Divider />

                {/* Cart items */}
                <Box sx={{ flex: 1, overflowY: 'auto', padding: 1 }}>
                    <Stack direction={'column'} spacing={1} padding={2}>
                        {cartItems?.map((item: any) => (
                            <Box key={item.eventDetails.eventID}>
                                <Stack direction={'row'} spacing={1} padding={'0px 10px'}>
                                    <Typography variant='subtitle1' sx={{ fontWeight: 500, flex: 4, textAlign: 'left' }}>{item.eventDetails.eventName}</Typography>
                                    <Typography variant='subtitle1' sx={{ fontWeight: 500, flex: 1, textAlign: 'left' }}>{formatCurrency(item.eventDetails.price)}</Typography>
                                    <StyledIconButton
                                        appTheme={appTheme}
                                        label={''}
                                        factor={1}
                                        color={'error'}
                                        width={25}
                                        height={25}
                                        icon={<DeleteOutlinedIcon sx={{ fontSize: 20 }} />}
                                        radius={10}
                                        onClick={() => {
                                            const newCartItems = cartItems.filter((i: any) => i.eventDetails.eventID !== item.eventDetails.eventID);
                                            setCartItems(newCartItems);
                                            createCookie(`${auth.user?.profile?.sub}-cartItems`, JSON.stringify(newCartItems), 30);
                                        }}
                                    />
                                </Stack>
                                <Accordion
                                    sx={{
                                        width: '100%',
                                        '&:before': { display: 'none' }
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            padding: 1,
                                            height: 35,
                                            minHeight: 'unset',
                                            '& .MuiAccordionSummary-content': {
                                                margin: 0,
                                                '&.Mui-expanded': { margin: 0 }
                                            },
                                            '&.Mui-expanded': { minHeight: 'unset', height: 35 }
                                        }}
                                    >
                                        <Typography variant="body2">Registration Details</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ padding: 1, flex: 1, maxHeight: '400px', overflowY: 'auto' }}>
                                        <Stack direction={'row'} spacing={1}>
                                            {item.registrationForm.map((registration: any, index: number) => (
                                                <Stack direction={'column'} spacing={1} key={index} flex={1}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        Player #{index + 1}
                                                    </Typography>
                                                    {Object.entries(registration)
                                                        .filter(([, value]) => {
                                                            if (value === null || value === undefined) return false;
                                                            if (value === '') return false;
                                                            if (typeof value === 'boolean') return value === true;
                                                            if (typeof value === 'object' && value !== null && 'value' in value) return (value as any).value != null && (value as any).value !== '';
                                                            return true;
                                                        })
                                                        .map(([key, value]: [string, any]) => (
                                                            <Stack direction={'column'} spacing={1} key={key}>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{key.replace(/([A-Z])/g, ' $1').trim().charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').trim().slice(1)} {value === true ? '✓' : ''}</Typography>
                                                                <Typography variant="body2" sx={{ textIndent: 5 }}>
                                                                    {value === null ? 'N/A' :
                                                                    typeof value === 'object' ? value.value :
                                                                    typeof value === 'string' && !isNaN(Number(value)) && value.toString().length > 5 ?
                                                                        `(${Number(value).toString().slice(0, 3)}) ${Number(value).toString().slice(3, 6)}-${Number(value).toString().slice(6)}` :
                                                                    value}
                                                                </Typography>
                                                            </Stack>
                                                        ))}
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                {/* Totals & checkout footer */}
                <Box sx={{ padding: 2, borderTop: 1, borderColor: 'divider' }}>
                    {loadingAdjustments && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <CircularProgress size={14} />
                            <Typography variant="body2" color="text.secondary">Checking available discounts…</Typography>
                        </Stack>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1fr) auto',
                            columnGap: 1,
                            rowGap: 1,
                            alignItems: 'center',
                            mb: 1,
                        }}
                    >
                        {cartItems.length > 0 && (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Subtotal
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: 'right' }}
                                >
                                    {formatCurrency(cartTotal)}
                                </Typography>
                            </>
                        )}

                        {applicableDiscounts.length > 0 && (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                    sx={{ gridColumn: '1 / -1' }}
                                >
                                    <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        Discounts Applied
                                    </Typography>
                                </Stack>
                                {applicableDiscounts.map((d: any) => (
                                    <React.Fragment key={d.discountID}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ pl: 1, minWidth: 0 }}
                                        >
                                            {formatDiscountLabel(d)}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="primary.main"
                                            sx={{ textAlign: 'right' }}
                                        >
                                            −{formatCurrency(discountLineAmount(d))}
                                        </Typography>
                                    </React.Fragment>
                                ))}
                            </>
                        )}

                        {availableCredits.length > 0 && (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                    sx={{ gridColumn: '1 / -1' }}
                                >
                                    <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        Credits Available: {formatCurrency(maxCreditsAvailable)}
                                    </Typography>
                                </Stack>
                                <Box sx={{ pl: 1, minWidth: 0 }}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Apply credits"
                                        value={creditAmountToApply === 0 ? '' : creditAmountToApply}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value) || 0;
                                            setCreditAmountToApply(
                                                Math.min(val, maxCreditsAvailable, Math.max(0, cartTotal - discountTotal))
                                            );
                                        }}
                                        inputProps={{
                                            min: 0,
                                            max: Math.min(maxCreditsAvailable, Math.max(0, cartTotal - discountTotal)),
                                            step: 0.01,
                                        }}
                                        sx={{ width: '100%', maxWidth: 200 }}
                                    />
                                </Box>
                                <Typography variant="body2" color="primary.main" sx={{ textAlign: 'right' }}>
                                    {safeCreditApplied > 0 ? `−${formatCurrency(safeCreditApplied)}` : '\u00a0'}
                                </Typography>
                            </>
                        )}
                    </Box>

                    {expiringCredits.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            {expiringCredits.map((c: any) => (
                                <Alert
                                    key={c.creditID}
                                    severity="warning"
                                    icon={<WarningAmberOutlinedIcon fontSize="small" />}
                                    sx={{ py: 0.5, mb: 1 }}
                                >
                                    <Typography variant="body2">
                                        {formatCurrency(c.remainingAmount)} expires on{' '}
                                        {formatCreditExpiryUtc(c.expiresAt)}
                                    </Typography>
                                </Alert>
                            ))}
                        </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* Final total */}
                    <Stack direction="column" spacing={2}>
                        <Typography variant="h6">
                            Total: {formatCurrency(finalTotal)}
                        </Typography>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleCheckout}
                            disabled={cartItems.length === 0}
                        >
                            Proceed to Checkout
                        </Button>
                    </Stack>
                </Box>
            </Drawer>
        </Box>
    );
};
